import React from 'react';
import { Stack, TextField, Box, FormControl, InputLabel, Select, MenuItem, Chip, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { ProcedureFormErrors } from '../hooks';
import { ProcedureType as ProcedureCodeType } from '../../../../../../../services/ContractService/types/contractEstimateTypes';

interface ProcedureDetailsFormProps {
    priority: number;
    units: number;
    implantCost: number;
    procedureType: ProcedureCodeType;
    billedCharges: number;
    revenueCode: string;
    modifiers: string[];
    apcCode: string;
    errors: ProcedureFormErrors;
    onPriorityChange: (value: number) => void;
    onUnitsChange: (value: number) => void;
    onImplantCostChange: (value: number) => void;
    onProcedureTypeChange: (value: ProcedureCodeType) => void;
    onBilledChargesChange: (value: number) => void;
    onRevenueCodeChange: (value: string) => void;
    onModifiersChange: (value: string[]) => void;
    onApcCodeChange: (value: string) => void;
}

export const ProcedureDetailsForm: React.FC<ProcedureDetailsFormProps> = ({
    priority,
    units,
    implantCost,
    procedureType,
    billedCharges,
    revenueCode,
    modifiers,
    apcCode,
    errors,
    onPriorityChange,
    onUnitsChange,
    onImplantCostChange,
    onProcedureTypeChange,
    onBilledChargesChange,
    onRevenueCodeChange,
    onModifiersChange,
    onApcCodeChange
}) => {
    // Handle modifier input as comma-separated values
    const handleModifiersInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Split by comma and trim whitespace
        const mods = value.split(',').map(m => m.trim()).filter(m => m.length > 0);
        onModifiersChange(mods);
    };

    return (
        <Stack spacing={3}>
            {/* Row 1: Basic Fields */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    label="Priority"
                    type="number"
                    value={priority}
                    onChange={(e) => onPriorityChange(parseInt(e.target.value) || 1)}
                    error={!!errors.priority}
                    helperText={errors.priority}
                    size="small"
                    sx={{ width: '120px' }}
                    inputProps={{ min: 1, step: 1 }}
                />
                <TextField
                    label="Units"
                    type="number"
                    value={units}
                    onChange={(e) => onUnitsChange(parseFloat(e.target.value) || 1)}
                    error={!!errors.units}
                    helperText={errors.units}
                    size="small"
                    sx={{ width: '120px' }}
                    inputProps={{ min: 1, step: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="procedure-type-label">Type</InputLabel>
                    <Select
                        labelId="procedure-type-label"
                        value={procedureType}
                        label="Type"
                        onChange={(e: SelectChangeEvent) => onProcedureTypeChange(e.target.value as ProcedureCodeType)}
                    >
                        <MenuItem value="CPT">CPT</MenuItem>
                        <MenuItem value="DRG">DRG</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {/* Row 2: Billing Fields */}
            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569' }}>
                    Billing Information
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        label="Billed Charges"
                        type="number"
                        value={billedCharges}
                        onChange={(e) => onBilledChargesChange(parseFloat(e.target.value) || 0)}
                        error={!!errors.billedCharges}
                        helperText={errors.billedCharges}
                        size="small"
                        sx={{ width: '160px' }}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>
                        }}
                    />
                    <TextField
                        label="Revenue Code"
                        value={revenueCode}
                        onChange={(e) => onRevenueCodeChange(e.target.value)}
                        error={!!errors.revenueCode}
                        helperText={errors.revenueCode}
                        size="small"
                        sx={{ width: '140px' }}
                        placeholder="e.g., 0360"
                    />
                    <TextField
                        label="Implant Cost"
                        type="number"
                        value={implantCost}
                        onChange={(e) => onImplantCostChange(parseFloat(e.target.value) || 0)}
                        error={!!errors.implantCost}
                        helperText={errors.implantCost}
                        size="small"
                        sx={{ width: '150px' }}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>
                        }}
                    />
                    <TextField
                        label="APC Override"
                        value={apcCode}
                        onChange={(e) => onApcCodeChange(e.target.value)}
                        size="small"
                        sx={{ width: '140px' }}
                        placeholder="e.g., 5114"
                        helperText="Optional"
                    />
                </Stack>
            </Box>

            {/* Row 3: Modifiers */}
            <Box>
                <TextField
                    label="Modifiers"
                    value={modifiers.join(', ')}
                    onChange={handleModifiersInput}
                    size="small"
                    fullWidth
                    placeholder="Enter comma-separated modifiers (e.g., 59, 26, TC)"
                    helperText="Separate multiple modifiers with commas"
                />
                {modifiers.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {modifiers.map((mod, idx) => (
                            <Chip 
                                key={idx} 
                                label={mod} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};
